// « Lis la file »: put 4 lead cards in the order the queue serves them. The right order is computed by the queue's
// own sort key (leadQueue.queueOrderKey, the TS twin of SQL lead_queue_key), so the game follows the code.
import {
  queueOrderKey,
  type QueueContext,
  type QueueLead,
} from "../../domain/leadQueue";

const K = "gavion.training.content.read_queue";
export const QUEUE_ME = 7;
export const QUEUE_NOW = new Date("2026-10-20T16:00:00Z");
const HOUR = 3600_000;
const ago = (hours: number) =>
  new Date(QUEUE_NOW.getTime() - hours * HOUR).toISOString();

export const queueContext = (): QueueContext => ({
  me: QUEUE_ME,
  now: QUEUE_NOW,
  maxAttempts: 5,
  suppressedPhones: new Set(),
});

const base = (id: number): QueueLead => ({
  id,
  assigned_sales_id: null,
  prospect_status: "in_progress",
  phone_e164: `+1514555${String(1000 + id)}`,
  phone_valid: true,
  cooldown_until: null,
  attempt_count: 0,
  next_callback_at: null,
  last_called_at: null,
  locked_by: null,
  locked_until: null,
  high_quality: false,
  fit_score: null,
  date_added: "2026-09-01",
});

export type QueueCard = {
  /** i18n key of the card's one-line description. */
  labelKey: string;
  labelArgs?: Record<string, string | number>;
  /** Company name shown on the card (fictional). */
  name: string;
  lead: QueueLead;
};
export type QueueRound = {
  id: string;
  /** Cards in the order they are first shown (shuffled by hand, never already right). */
  cards: readonly QueueCard[];
  explanationKey: string;
};

const card = (
  name: string,
  label: string,
  lead: Partial<QueueLead> & { id: number },
  labelArgs?: QueueCard["labelArgs"],
): QueueCard => ({
  name,
  labelKey: `${K}.cards.${label}`,
  ...(labelArgs ? { labelArgs } : {}),
  lead: { ...base(lead.id), ...lead },
});

export const QUEUE_ROUNDS: readonly QueueRound[] = [
  {
    id: "r1",
    explanationKey: `${K}.r1.explanation`,
    cards: [
      card(
        "Toitures Bédard",
        "retry",
        { id: 1, attempt_count: 1, last_called_at: ago(48) },
        { hours: 48 },
      ),
      card("Plomberie Gagnon", "never_hq", {
        id: 2,
        high_quality: true,
        fit_score: 80,
      }),
      card(
        "Électricité Roy",
        "callback_unassigned",
        {
          id: 3,
          attempt_count: 1,
          next_callback_at: ago(3),
          last_called_at: ago(30),
        },
        { hours: 3 },
      ),
      card(
        "Paysagement Côté",
        "callback_mine",
        {
          id: 4,
          assigned_sales_id: QUEUE_ME,
          attempt_count: 2,
          next_callback_at: ago(1),
          last_called_at: ago(26),
        },
        { hours: 1 },
      ),
    ],
  },
  {
    id: "r2",
    explanationKey: `${K}.r2.explanation`,
    cards: [
      card(
        "Chauffage Lavoie",
        "never_score",
        { id: 11, fit_score: 40 },
        { score: 40 },
      ),
      card(
        "Déneigement Pelletier",
        "retry",
        { id: 12, attempt_count: 2, last_called_at: ago(72) },
        { hours: 72 },
      ),
      card(
        "Rénovations Morin",
        "never_hq_score",
        { id: 13, high_quality: true, fit_score: 20 },
        { score: 20 },
      ),
      card(
        "Climatisation Fortin",
        "callback_unassigned_min",
        {
          id: 14,
          attempt_count: 1,
          next_callback_at: ago(1 / 6),
          last_called_at: ago(28),
        },
        { minutes: 10 },
      ),
    ],
  },
  {
    id: "r3",
    explanationKey: `${K}.r3.explanation`,
    cards: [
      card(
        "Excavation Girard",
        "retry",
        { id: 21, attempt_count: 1, last_called_at: ago(30) },
        { hours: 30 },
      ),
      card("Peinture Ouellet", "never_hq_unassigned", {
        id: 22,
        high_quality: true,
        fit_score: 95,
      }),
      card(
        "Gouttières Caron",
        "retry",
        { id: 23, attempt_count: 3, last_called_at: ago(96) },
        { hours: 96 },
      ),
      card(
        "Portes Gauthier",
        "never_mine_score",
        { id: 24, assigned_sales_id: QUEUE_ME, fit_score: 60 },
        { score: 60 },
      ),
    ],
  },
];

/** The order the queue serves the round's cards (ids). Every card of a round is queue-eligible. */
export function servedOrder(round: QueueRound): number[] {
  const ctx = queueContext();
  return [...round.cards]
    .map((c) => ({ id: c.lead.id, key: queueOrderKey(c.lead, ctx) }))
    .sort((a, b) => (a.key! < b.key! ? -1 : a.key! > b.key! ? 1 : 0))
    .map((x) => x.id);
}

/** Minimum gap before a lead already called comes back as a retry (taught as « 20 h »; checked by the tests). */
export const RETRY_GAP_HOURS = 20;
